// screens-home.tsx - Marketing: Home / Landing
// Exports: ScreenHome, HOME_STEPS, HOME_PROOF, HOME_CAPABILITIES
import type { ReactNode } from 'react';
import { Link } from '../design/router';
import { Arrow, CtaBand, Glyph, MarketingFooter, MarketingNav, PlayCta, SignupCta } from '../components/ui-shared';
import { SIGNUP_OPEN } from '../config/studioStatus';
import { ImageSlot } from '../components/ImageSlot';
import { keyArt, mediaSrc, useOriginalMedia } from '../showcase/media';
import { HeroStudio, HeroStudioMobile } from './HeroStudio';

/* ── hero ─────────────────────────────────────────────────────────────── */
function HomeHero() {
  return (
    <header style={{ padding: '92px clamp(20px, 6vw, 56px) 0', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'radial-gradient(ellipse 65% 50% at 50% -8%, var(--ss-glow), transparent 70%)' }} />
      <div style={{ position: 'relative', maxWidth: 1080, margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 26 }}>
        <span className="ss-eyebrow">Build · Host · Install · Play</span>
        <h1 className="ss-display" style={{ fontSize: 'clamp(2.4rem, 7vw, 4.25rem)' }}>
          Make games in your browser.<br className="ss-hide-mobile" /> Ship them as real apps.
        </h1>
        <p style={{ color: 'var(--ss-text-2)', fontSize: 18.5, maxWidth: 640, textWrap: 'pretty' }}>
          A complete 2D and 3D game studio in a tab. Build worlds, characters, rules and multiplayer
          without code, then publish once: your game is hosted at its own address and installs on
          phones, tablets and desktops like any other app.
        </p>
        <div style={{ display: 'flex', gap: 12, marginTop: 6, flexWrap: 'wrap', justifyContent: 'center' }}>
          <SignupCta label="Start building free" className="ss-btn ss-btn--primary ss-btn--lg" />
          <Link className="ss-btn ss-btn--ghost ss-btn--lg" to="/features">See what is inside</Link>
        </div>
        <p style={{ color: 'var(--ss-text-3)', fontSize: 13 }}>
          {SIGNUP_OPEN
            ? 'Free to start · Nothing to install for you · Players need only a browser'
            : 'In active development · Sign-ups opening soon · Players need only a browser'}
        </p>
      </div>
      {/* The full interactive editor needs real width for its side panels;
          below 720px it swaps for HeroStudioMobile's single-pane version
          instead of disappearing outright (.hm-full/.hm-mobile in
          soulstack.css). */}
      <div className="hm-full" style={{ position: 'relative', maxWidth: 1150, margin: '64px auto 0' }}>
        <p style={{ fontSize: 12.5, color: 'var(--ss-text-3)', marginBottom: 14 }}>
          This is a working miniature of the editor. Drag the model, switch workspaces, test a rule, run a script, press Publish.
        </p>
        <HeroStudio />
      </div>
      <div className="hm-mobile" style={{ position: 'relative', maxWidth: 480, margin: '44px auto 0' }}>
        <p style={{ fontSize: 12.5, color: 'var(--ss-text-3)', marginBottom: 12 }}>A working miniature of the editor. Drag to orbit, tap to select, tap a colour.</p>
        <HeroStudioMobile />
      </div>
    </header>
  );
}

/* ── proof strip ──────────────────────────────────────────────────────── */
export const HOME_PROOF: { n: string; l: string }[] = [
  { n: '2D + 3D', l: 'One studio, both projections' },
  { n: '0 installs', l: 'For you. Optional for players' },
  { n: '45 clips', l: 'Rigged biped in every project' },
  { n: '5 games', l: 'Shipped on the platform already' },
];

function HomeProof() {
  return (
    <section style={{ padding: '56px clamp(20px, 6vw, 56px) 0' }}>
      <div className="ss-grid-collapse" style={{ maxWidth: 1180, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
        {HOME_PROOF.map((p) => (
          <div key={p.n} className="ss-card" style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span className="ss-display" style={{ fontSize: 24 }}>{p.n}</span>
            <span style={{ fontSize: 12.5, color: 'var(--ss-text-3)' }}>{p.l}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ── capability grid ──────────────────────────────────────────────────── */
export interface HomeCapability {
  title: string;
  copy: string;
  to: string;
  points: string[];
}

export const HOME_CAPABILITIES: HomeCapability[] = [
  {
    title: 'Scene editing, live',
    to: '/features/editor',
    copy: 'Place props, draw walls and floors, light the scene and drop trigger zones inside the running world. Press Preview and walk it.',
    points: ['Snap, grid and gizmo tools', 'Multi-floor maps with stairs', 'Undo, autosave and scene history'],
  },
  {
    title: 'Art without leaving the tab',
    to: '/features/art',
    copy: 'Block out 3D models from primitives, paint pixel sprites and tilemaps, or upload your own GLB files. A shared library adds thousands of ready models.',
    points: ['Model Studio with boolean ops', 'Pixel Studio with clips and palettes', 'Material and texture presets'],
  },
  {
    title: 'Characters that already move',
    to: '/features/characters',
    copy: 'Every project ships a rigged biped with 45 clips. Map your own model onto it, or auto-rig it, and the whole library plays on your art.',
    points: ['Locomotion, combat, social, traversal', 'Custom rig mapping and auto-rig', 'Patrol routes and placements'],
  },
  {
    title: 'Rules, not code',
    to: '/features/logic',
    copy: 'Gameplay is written as plain when, if, then sentences. Zones, scores, items, quests and win conditions, all from a picker. Scripts when you want more.',
    points: ['8 events, 10 facts, 11 actions', 'HUD, audio, data and cutscenes', 'HollowScript with granted capabilities'],
  },
  {
    title: 'Multiplayer built in',
    to: '/features/multiplayer',
    copy: 'Flip a project to Co-op or Versus and players see each other move and share rule state automatically. Nothing to author, nothing to host.',
    points: ['Presence and live movement', 'Shared variables, quests and outcomes', 'Players join from a link, no account'],
  },
  {
    title: 'Hosted and installable',
    to: '/features/hosting',
    copy: 'One publish puts the game at its own address, kept for every version. Players open it in a browser or install it to their home screen.',
    points: ['Public, unlisted or off', 'Every version restorable', 'Install on desktop and mobile'],
  },
];

function HomeCapabilities() {
  return (
    <section className="mk-section">
      <div className="mk-inner">
        <div className="mk-section-head">
          <span className="ss-eyebrow ss-eyebrow--dim">The studio</span>
          <h2 className="ss-display">Everything a game needs, in one place.</h2>
          <p>Five workspaces cover the whole job: Scene, Art & Assets, Systems, Setup and Code. Switch between them without a rebuild, an export or a second application.</p>
        </div>
        <div className="ss-grid-collapse" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 18 }}>
          {HOME_CAPABILITIES.map((c) => (
            <Link key={c.title} to={c.to} className="bento-card">
              <h3 className="ss-display" style={{ fontSize: 21 }}>{c.title}</h3>
              <p style={{ color: 'var(--ss-text-2)', fontSize: 13.5, lineHeight: 1.55, textWrap: 'pretty' }}>{c.copy}</p>
              <ul style={{ margin: '4px 0 0', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
                {c.points.map((p) => (
                  <li key={p} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--ss-text-2)' }}>
                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--ss-accent)', flexShrink: 0 }} />{p}
                  </li>
                ))}
              </ul>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12.5, fontWeight: 600, color: 'var(--ss-accent)', marginTop: 'auto', paddingTop: 10 }}>
                Learn more <Arrow />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── ship band: what a player actually gets ───────────────────────────── */
function ShipCard({ title, copy, children }: { title: string; copy: string; children: ReactNode }) {
  return (
    <div className="ss-card" style={{ padding: '24px 26px', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ height: 96, borderRadius: 12, background: 'var(--ss-bg-2)', border: '1px solid var(--ss-border-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
        {children}
      </div>
      <h3 style={{ fontSize: 16, fontWeight: 600, marginTop: 6 }}>{title}</h3>
      <p style={{ fontSize: 13.5, color: 'var(--ss-text-2)', lineHeight: 1.55, textWrap: 'pretty' }}>{copy}</p>
    </div>
  );
}

function PhoneGlyph() {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10 }}>
      <div style={{ width: 34, height: 60, borderRadius: 8, border: '1.5px solid var(--ss-text-3)', position: 'relative' }}>
        <span style={{ position: 'absolute', left: '50%', top: 8, transform: 'translateX(-50%)', width: 14, height: 14, borderRadius: 4, background: 'var(--ss-accent)' }} />
        <span style={{ position: 'absolute', left: '50%', top: 25, transform: 'translateX(-50%)', width: 16, height: 2, borderRadius: 1, background: 'var(--ss-border-strong)' }} />
      </div>
      <div style={{ width: 84, height: 54, borderRadius: 6, border: '1.5px solid var(--ss-text-3)', position: 'relative' }}>
        <span style={{ position: 'absolute', left: 8, top: 8, width: 14, height: 14, borderRadius: 4, background: 'var(--ss-accent)' }} />
        <span style={{ position: 'absolute', left: 8, top: 26, width: 30, height: 2, borderRadius: 1, background: 'var(--ss-border-strong)' }} />
      </div>
    </div>
  );
}

function UrlGlyph() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center' }}>
      <div className="hm-stub" style={{ padding: '7px 12px', fontFamily: 'var(--ss-mono)', fontSize: 11, color: 'var(--ss-text)' }}>sstk.studio/play/your-game</div>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 9, fontWeight: 600, letterSpacing: '0.12em', color: 'var(--ss-success)' }}>
        <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--ss-success)' }} />LIVE · V12
      </span>
    </div>
  );
}

function VersionsGlyph() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, width: 150 }}>
      {[['v12', 'Live', true], ['v11', 'Restore', false], ['v10', 'Restore', false]].map(([v, a, live]) => (
        <div key={String(v)} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, padding: '4px 8px', borderRadius: 6, background: live ? 'var(--ss-accent-soft)' : 'var(--ss-hover)', color: live ? 'var(--ss-text)' : 'var(--ss-text-2)' }}>
          <span style={{ fontFamily: 'var(--ss-mono)' }}>{v}</span><span style={{ color: live ? 'var(--ss-success)' : 'var(--ss-accent)', fontWeight: 600 }}>{a}</span>
        </div>
      ))}
    </div>
  );
}

function HomeShip() {
  return (
    <section className="mk-section" style={{ paddingTop: 0 }}>
      <div className="mk-inner">
        <div className="mk-section-head">
          <span className="ss-eyebrow ss-eyebrow--dim">Publishing</span>
          <h2 className="ss-display">A real game at the end, not a demo link.</h2>
          <p>Publishing is not an export step you run somewhere else. It is one button, and what comes out is hosted, versioned and installable.</p>
        </div>
        <div className="ss-grid-collapse" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 18 }}>
          <ShipCard title="Hosted at its own address" copy="Every project claims a permanent play address the first time you publish. Players open it and the game streams into the tab. No launcher, no download, no account.">
            <UrlGlyph />
          </ShipCard>
          <ShipCard title="Installs like an app" copy="Published games carry an app manifest, so players can add them to a phone home screen or a desktop. It opens full screen with your icon and name.">
            <PhoneGlyph />
          </ShipCard>
          <ShipCard title="Every version kept" copy="Each publish is a snapshot with a note. Roll back in one click, compare what changed, and take the play address with you when you rename the game.">
            <VersionsGlyph />
          </ShipCard>
        </div>
      </div>
    </section>
  );
}

/* ── statement band ───────────────────────────────────────────────────── */
function HomeStatement() {
  return (
    <section style={{ padding: '104px clamp(20px, 6vw, 56px)', borderTop: '1px solid var(--ss-border-soft)', textAlign: 'center' }}>
      <div style={{ maxWidth: 760, margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18 }}>
        <h2 className="ss-display" style={{ fontSize: 'clamp(2rem, 6vw, 2.875rem)' }}>Nothing to install to make it.<br className="ss-hide-mobile" /> Everything needed to install it.</h2>
        <p style={{ fontFamily: 'var(--ss-sans)', fontWeight: 500, fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ss-text-3)' }}>
          Any device with a browser · Fast first frame · Your game, your address
        </p>
      </div>
    </section>
  );
}

/* ── how it works ─────────────────────────────────────────────────────── */
export const HOME_STEPS = [
  { n: '1', title: 'Create a project', copy: 'Start from Blank, Gallery, Village or Platformer. Pick 2D or 3D and the studio adapts. Everything runs in the browser.' },
  { n: '2', title: 'Build and playtest', copy: 'Edit the world, model and paint, pose characters, write rules. Press Preview and you are in the game. Escape brings you back with nothing lost.' },
  { n: '3', title: 'Publish', copy: 'Press Publish, write a note, choose who can see it. Your game is live at its own address, installable, with multiplayer ready when you want it.' },
];

function HomeSteps() {
  const steps = HOME_STEPS;
  return (
    <section className="mk-section" style={{ paddingTop: 0 }}>
      <div className="mk-inner">
        <hr className="ss-hairline" style={{ marginBottom: 92 }} />
        <div className="ss-grid-collapse" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 56 }}>
          {steps.map((s) => (
            <div key={s.n} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <span style={{
                width: 34, height: 34, borderRadius: '50%', border: '1px solid var(--ss-border)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: 'var(--ss-sans)', fontWeight: 500, fontSize: 13, color: 'var(--ss-accent)',
              }}>{s.n}</span>
              <h3 style={{ fontSize: 18, fontWeight: 600 }}>{s.title}</h3>
              <p style={{ color: 'var(--ss-text-2)', fontSize: 14.5, textWrap: 'pretty' }}>{s.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Originals band ───────────────────────────────────────────────────── */
export const HOME_ORIGINALS: { name: string; genre: string; to: string }[] = [
  { name: 'Sunder', genre: 'Tactical FPS · 5v5', to: '/sunder' },
  { name: 'Nine Lives', genre: '2D platformer', to: '/ninelives' },
  { name: 'Furrow', genre: 'Co-op farming · 1 to 4', to: '/furrow' },
  { name: 'The Dwindling', genre: 'First-person horror', to: '/dwindling' },
  { name: 'Mewsli', genre: 'Match-3', to: '/mewsli' },
];

function HomeOriginalsBand() {
  // The band's art is Sunder's own key art, the same frame the Showcase
  // hero uses, rather than a placeholder on the site's first screen.
  const sunder = useOriginalMedia('sunder');
  const art = keyArt(sunder);
  return (
    <section style={{ position: 'relative', borderTop: '1px solid var(--ss-border-soft)', borderBottom: '1px solid var(--ss-border-soft)', background: 'var(--ss-bg)' }}>
      <div className="home-sunder-grid" style={{ maxWidth: 1180, margin: '0 auto', padding: '96px 56px', display: 'grid', gridTemplateColumns: '440px 1fr', gap: 72, alignItems: 'center' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <span className="ss-eyebrow">Soulstack Originals</span>
          <h2 className="ss-display" style={{ fontSize: 'clamp(2rem, 6vw, 3rem)' }}>Five games already live on it.</h2>
          <p style={{ color: 'var(--ss-text-2)', fontSize: 16.5, textWrap: 'pretty' }}>
            We build and ship our own games on the same engine, editor and hosting you get. Sunder, our
            tactical shooter, runs on a dedicated match server. The rest run exactly the way your project will.
          </p>
          <div style={{ display: 'flex', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
            <PlayCta href="https://sunder.sstk.studio">Play Sunder</PlayCta>
            <Link className="ss-btn ss-btn--ghost" to="/showcase">Browse the showcase</Link>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <ImageSlot shape="rounded" radius={18} placeholder="Sunder key art"
            src={mediaSrc(art?.url) ?? undefined} alt={art?.alt ?? 'Sunder key art'}
            style={{ width: '100%', height: 260, display: 'block' }} />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {HOME_ORIGINALS.map((g) => (
              <Link key={g.name} to={g.to} className="ss-card" style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 2, flex: '1 1 150px', color: 'var(--ss-text)' }}>
                <span style={{ fontSize: 13.5, fontWeight: 600 }}>{g.name}</span>
                <span style={{ fontSize: 11.5, color: 'var(--ss-text-3)' }}>{g.genre}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── docs pointer ─────────────────────────────────────────────────────── */
function HomeDocsPointer() {
  const links = [
    { t: 'Quickstart', c: 'Account to published game, step by step.', to: '/docs/quickstart' },
    { t: 'The editor', c: 'Every workspace, panel and shortcut.', to: '/docs/editor' },
    { t: 'Publishing', c: 'Addresses, visibility, versions and installs.', to: '/docs/publishing' },
  ];
  return (
    <section className="mk-section" style={{ paddingTop: 72, paddingBottom: 72 }}>
      <div className="mk-inner" style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Glyph size={22} />
          <span className="ss-eyebrow ss-eyebrow--dim">Documentation</span>
        </div>
        <div className="ss-grid-collapse" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
          {links.map((l) => (
            <Link key={l.t} to={l.to} className="ss-card" style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 6, color: 'var(--ss-text)' }}>
              <span style={{ fontSize: 15, fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>{l.t}<Arrow /></span>
              <span style={{ fontSize: 13, color: 'var(--ss-text-3)' }}>{l.c}</span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export function ScreenHome() {
  return (
    <div className="ss-screen">
      <MarketingNav />
      <HomeHero />
      <HomeProof />
      <HomeCapabilities />
      <HomeShip />
      <HomeStatement />
      <HomeSteps />
      <HomeOriginalsBand />
      <HomeDocsPointer />
      <CtaBand />
      <MarketingFooter />
    </div>
  );
}
